/**
 * Tempo detection + pitch-preserving time-stretch.
 *
 * Two exports:
 *   - `detectTempo(buffer)` → { bpm, confidence }
 *   - `timeStretch(buffer, factor)` → new AudioBuffer at the same pitch
 *     but `factor`× the original duration. Uses SOLA (Synchronized
 *     OverLap-Add): chop into ~46 ms grains, slide each by `factor` × hop,
 *     find the best-correlated alignment in a small search window, then
 *     crossfade. No external deps; runs in ~80 ms per second of audio.
 *
 * `detectTempo` uses spectral-flux onset detection (FFT shared with
 * keyDetection.js's algorithm) → autocorrelate the ODF → peak picks in
 * the 60-180 BPM range.
 */

/** Reuse the FFT from keyDetection — re-export lazily to avoid a cycle. */
function makeFft(N) {
  const rev = new Uint32Array(N);
  for (let i = 0; i < N; i++) {
    let j = 0, n = i;
    for (let k = 1; k < N; k <<= 1) { j = (j << 1) | (n & 1); n >>= 1; }
    rev[i] = j;
  }
  return function fft(real, imag) {
    for (let i = 0; i < N; i++) {
      const j = rev[i];
      if (j > i) { [real[i], real[j]] = [real[j], real[i]]; [imag[i], imag[j]] = [imag[j], imag[i]]; }
    }
    for (let size = 2; size <= N; size <<= 1) {
      const half = size >> 1;
      const ang = -2 * Math.PI / size;
      const wpr = Math.cos(ang), wpi = Math.sin(ang);
      for (let start = 0; start < N; start += size) {
        let wr = 1, wi = 0;
        for (let k = 0; k < half; k++) {
          const i1 = start + k, i2 = i1 + half;
          const tr = wr * real[i2] - wi * imag[i2];
          const ti = wr * imag[i2] + wi * real[i2];
          real[i2] = real[i1] - tr; imag[i2] = imag[i1] - ti;
          real[i1] += tr; imag[i1] += ti;
          const newWr = wr * wpr - wi * wpi;
          wi = wr * wpi + wi * wpr; wr = newWr;
        }
      }
    }
  };
}

/** Spectral-flux onset detection function. Returns Float32Array, one
 *  value per ~11.6 ms frame (= hop / sr). High values = beat-like onsets. */
export function onsetEnvelope(buffer) {
  const sr = buffer.sampleRate;
  const ch0 = buffer.getChannelData(0);
  const samples = ch0.length;
  const winSize = 1024;
  const hop = 512;                  // 50% overlap, ~11.6 ms at 44.1 kHz
  const numFrames = Math.floor((samples - winSize) / hop);
  const env = new Float32Array(numFrames);
  const fft = makeFft(winSize);
  const window = new Float32Array(winSize);
  for (let i = 0; i < winSize; i++) window[i] = 0.5 * (1 - Math.cos((2 * Math.PI * i) / (winSize - 1)));
  const real = new Float32Array(winSize), imag = new Float32Array(winSize);
  const prevMag = new Float32Array(winSize / 2);
  for (let f = 0; f < numFrames; f++) {
    const start = f * hop;
    for (let i = 0; i < winSize; i++) {
      real[i] = ch0[start + i] * window[i];
      imag[i] = 0;
    }
    fft(real, imag);
    let flux = 0;
    for (let k = 0; k < winSize / 2; k++) {
      const mag = Math.sqrt(real[k] * real[k] + imag[k] * imag[k]);
      // Half-wave rectified diff — only count energy INCREASES.
      const diff = mag - prevMag[k];
      if (diff > 0) flux += diff;
      prevMag[k] = mag;
    }
    env[f] = flux;
  }
  return { env, hopSec: hop / sr };
}

/** Estimate tempo (BPM) of an AudioBuffer by autocorrelating its onset
 *  envelope and finding the peak in the 60-180 BPM range. */
export function detectTempo(buffer) {
  const { env, hopSec } = onsetEnvelope(buffer);
  if (env.length < 50) return { bpm: 120, confidence: 0 };

  // Normalise + detrend (subtract mean).
  let mean = 0;
  for (let i = 0; i < env.length; i++) mean += env[i];
  mean /= env.length;
  const e = new Float32Array(env.length);
  for (let i = 0; i < env.length; i++) e[i] = Math.max(0, env[i] - mean);

  // Autocorrelate in the lag range that maps to 60..200 BPM.
  // tau = 60 / (BPM * hopSec) frames per beat
  const minLag = Math.floor(60 / (200 * hopSec));  // 200 BPM
  const maxLag = Math.ceil(60 / (60 * hopSec));    // 60 BPM
  let bestLag = minLag;
  let bestCorr = -1;
  for (let lag = minLag; lag <= maxLag; lag++) {
    let corr = 0, denom = 0;
    for (let i = 0; i + lag < e.length; i++) {
      corr += e[i] * e[i + lag];
      denom += e[i] * e[i];
    }
    const norm = denom > 0 ? corr / denom : 0;
    if (norm > bestCorr) { bestCorr = norm; bestLag = lag; }
  }
  const bpmRaw = 60 / (bestLag * hopSec);
  // Octave error correction — half-tempo is the most common mistake.
  // If the double is in the 60-180 sweet spot, prefer it.
  let bpm = bpmRaw;
  if (bpmRaw < 90 && bpmRaw * 2 <= 180) bpm = bpmRaw * 2;
  if (bpmRaw > 180 && bpmRaw / 2 >= 60) bpm = bpmRaw / 2;
  return {
    bpm: Math.round(bpm * 10) / 10,
    confidence: Math.max(0, Math.min(1, bestCorr)),
  };
}

/** SOLA (Synchronized OverLap-Add) time-stretch — preserves pitch.
 *  @param {AudioBuffer} buffer
 *  @param {number} factor — output duration as a multiple of input
 *         (factor > 1 → longer, factor < 1 → shorter)
 *  @returns {Promise<AudioBuffer>} */
export async function timeStretch(buffer, factor) {
  if (Math.abs(factor - 1) < 0.02) return buffer;
  if (factor <= 0.25 || factor > 4) {
    // Extreme stretches sound terrible with SOLA — clamp.
    factor = Math.max(0.25, Math.min(4, factor));
  }
  const sr = buffer.sampleRate;
  const channels = buffer.numberOfChannels;
  const grain = Math.floor(0.046 * sr);          // 46 ms grain
  const synHop = Math.floor(grain / 2);          // 23 ms synthesis hop
  const anaHop = Math.floor(synHop / factor);    // analysis hop
  const search = Math.floor(0.010 * sr);         // ±10 ms cross-correlation search

  const inLen = buffer.length;
  const outLen = Math.max(1, Math.floor(inLen * factor));
  const ctx = new OfflineAudioContext(channels, outLen, sr);
  // Hann fade-in / fade-out for cross-fade region.
  const fadeLen = grain - synHop;
  const fadeIn = new Float32Array(fadeLen);
  const fadeOut = new Float32Array(fadeLen);
  for (let i = 0; i < fadeLen; i++) {
    fadeIn[i] = 0.5 * (1 - Math.cos((Math.PI * i) / fadeLen));
    fadeOut[i] = 0.5 * (1 + Math.cos((Math.PI * i) / fadeLen));
  }
  const out = ctx.createBuffer(channels, outLen, sr);

  for (let ch = 0; ch < channels; ch++) {
    const input = buffer.getChannelData(ch);
    const output = out.getChannelData(ch);
    let anaPos = 0;
    let synPos = 0;
    let prevGrain = null;
    while (synPos + grain < outLen) {
      // Find best-correlated position within ±search window
      let bestOff = 0;
      if (prevGrain && search > 0) {
        let bestCorr = -Infinity;
        const lo = Math.max(0, anaPos - search);
        const hi = Math.min(inLen - grain, anaPos + search);
        for (let off = lo; off <= hi; off++) {
          // Correlate the FIRST fadeLen samples of [input[off..]] against
          // the LAST fadeLen samples of prevGrain (the part being faded out).
          let c = 0;
          for (let i = 0; i < fadeLen; i++) {
            c += input[off + i] * prevGrain[synHop + i];
          }
          if (c > bestCorr) { bestCorr = c; bestOff = off; }
        }
      } else {
        bestOff = Math.max(0, Math.min(inLen - grain, anaPos));
      }
      // Write the grain into output, crossfading with whatever's there
      const thisGrain = new Float32Array(grain);
      for (let i = 0; i < grain; i++) thisGrain[i] = input[bestOff + i] || 0;
      for (let i = 0; i < grain; i++) {
        if (i < fadeLen && synPos + i < outLen) {
          // Crossfade region — sum input * fadeIn + existing output * fadeOut
          const existing = output[synPos + i] || 0;
          output[synPos + i] = existing * fadeOut[i] + thisGrain[i] * fadeIn[i];
        } else if (synPos + i < outLen) {
          output[synPos + i] = thisGrain[i];
        }
      }
      prevGrain = thisGrain;
      anaPos += anaHop;
      synPos += synHop;
    }
  }
  return out;
}

/** Compute the optimal time-stretch factor so the TTS phrase fits an
 *  integer number of bars at `targetBpm`. Picks the bar count that
 *  results in the smallest stretch (closest to 1.0). */
export function fitToBarGrid(phraseDuration, targetBpm, { minBars = 1, maxBars = 4 } = {}) {
  const barSec = (60 / targetBpm) * 4;  // 4 beats per bar
  let bestFactor = 1, bestBars = 1, bestDist = Infinity;
  for (let bars = minBars; bars <= maxBars; bars++) {
    const target = bars * barSec;
    const factor = target / phraseDuration;
    if (factor < 0.5 || factor > 2.0) continue;  // skip extreme stretches
    const dist = Math.abs(Math.log(factor));
    if (dist < bestDist) { bestDist = dist; bestFactor = factor; bestBars = bars; }
  }
  return { factor: bestFactor, bars: bestBars };
}
